import React from 'react';

type Props = {
  className?: string;
  elementId: string;
};

/**
 * Shows a server-rendered element inside a React layout without re-rendering it.
 *
 * React owns only the host element. The PHP markup inside it, including its forms,
 * inline script state and the React roots PHP mounted into it, is moved rather than
 * copied, so it keeps its identity, values and event listeners.
 */
export function LegacyContentHost({ className, elementId }: Props) {
  const hostRef = React.useRef<HTMLDivElement>(null);

  // A layout effect, because its cleanup runs before React removes the host. A
  // passive cleanup would run after the host, and the content with it, had left
  // the document.
  React.useLayoutEffect(() => {
    const host = hostRef.current;
    const content = document.getElementById(elementId);
    if (!host || !content) {
      throw new Error(`Legacy content #${elementId} is missing`);
    }

    const originalParent = content.parentNode;
    const originalNextSibling = content.nextSibling;
    host.appendChild(content);
    content.hidden = false;

    return () => {
      content.hidden = true;
      // Returning the node lets the next host (for example the fallback layout
      // after a failed implementation) attach the same live content.
      originalParent?.insertBefore(
        content,
        originalNextSibling?.parentNode === originalParent
          ? originalNextSibling
          : null,
      );
    };
  }, [elementId]);

  return <div ref={hostRef} className={className} />;
}
